/**
 * MockWidget — las dos maquetas del widget de reseñas, copiadas del widget
 * REAL y no del diseño.
 *
 * ── POR QUÉ EXISTE ESTE FICHERO ─────────────────────────────────────────────
 *
 * La primera versión de estas maquetas salió de `Widget de reseñas.dc.html`, y
 * el diseño se lo había inventado: tarjeta centrada, nombre del negocio en
 * mayúsculas con tracking, cinco estrellas sueltas al lado de la nota, un logo
 * de Google diminuto debajo. El widget de verdad no se parece en casi nada.
 *
 * Y esto importa más que en el resto de la página: es la ÚNICA imagen que un
 * visitante tiene de lo que va a poder pegar en su web. Si lo que instala no se
 * parece a lo que vio, la landing le prometió otra cosa.
 *
 * Así que todo lo de aquí está tomado de las plantillas reales:
 *
 *   QR/gbusiness/templates/gbusiness/widgets/base.html      (tokens y estrellas)
 *   QR/gbusiness/templates/gbusiness/widgets/summary.html   (el resumen)
 *   QR/gbusiness/templates/gbusiness/widgets/carousel.html  (el carrusel)
 *
 * Si alguien toca esas plantillas, esto se queda viejo en silencio. No hay
 * forma de atarlo con un test —son dos lenguajes y dos procesos distintos—, así
 * que queda dicho aquí: **al cambiar el widget, cambiar también esto.**
 *
 * Lo que se pinta son los valores POR DEFECTO de `validate_theme`: tema claro,
 * acento de marca, radio 12 px, avatares y fechas visibles.
 */

/**
 * Los tokens del widget, con el nombre que tienen en `base.html`.
 *
 * Van como objeto y no como clases de Tailwind a propósito: escritos así se ve
 * de un vistazo que son los MISMOS valores que el CSS del widget, y comparar
 * los dos ficheros es leer dos listas, no descifrar `text-[#1f2937]`.
 */
const T = {
  card: '#ffffff',      // --mts-card
  fg: '#1f2937',        // --mts-fg
  muted: '#6b7280',     // --mts-muted
  border: '#e5e7eb',    // --mts-border
  accent: '#feae2c',    // --mts-accent
  star: '#feae2c',      // --mts-star
  starEmpty: '#d8dadf', // --mts-star-empty
  radius: 12,           // --mts-radius (por defecto; el usuario lo mueve de 0 a 24)
  shadow: '0 1px 3px rgba(0,0,0,.08), 0 1px 2px rgba(0,0,0,.04)', // --mts-shadow
};

/** `star_pct` de `widgets.py`: el relleno es la nota sobre 5, en porcentaje. */
const porcentaje = (nota) => Math.round(Math.max(0, Math.min(5, nota)) / 5 * 100);

/**
 * Las estrellas del widget: NO son cinco iconos, son dos filas de «★★★★★»
 * superpuestas donde la de arriba se recorta al porcentaje de la nota. Por eso
 * un 4,7 enseña casi cinco estrellas y no cinco enteras ni cuatro y media.
 */
function EstrellasWidget({ nota, etiqueta }) {
  return (
    <span
      className="relative inline-block whitespace-nowrap leading-none"
      style={{ fontSize: 18, letterSpacing: 2 }}
      role="img"
      aria-label={etiqueta}
    >
      <span aria-hidden="true" style={{ color: T.starEmpty }}>★★★★★</span>
      <span
        aria-hidden="true"
        className="absolute top-0 left-0 overflow-hidden whitespace-nowrap"
        style={{ width: `${porcentaje(nota)}%`, color: T.star }}
      >
        ★★★★★
      </span>
    </span>
  );
}

/**
 * El widget de RESUMEN.
 *
 * Cuatro cosas que el diseño tenía al revés y aquí no:
 *   1. Va alineado a la IZQUIERDA, no centrado.
 *   2. El nombre del negocio va tal cual, 15 px y negrita — ni mayúsculas ni
 *      tracking.
 *   3. La nota es grande (34 px) y va en el COLOR DE ACENTO, que por defecto es
 *      el ámbar de marca y el usuario puede cambiar.
 *   4. La G de Google es una marca de agua de 112 px que se sale por la esquina
 *      inferior derecha y queda DETRÁS del contenido, no un icono centrado.
 */
export function WidgetResumen({ negocio, nota, total, conMarca = true }) {
  return (
    <div>
      <div
        className="relative overflow-hidden flex flex-col"
        style={{
          gap: 10,
          background: T.card,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          padding: '16px 18px',
          boxShadow: T.shadow,
          color: T.fg,
        }}
      >
        <GoogleMarcaDeAgua />

        <div className="relative z-[1] font-bold" style={{ fontSize: 15, lineHeight: 1.25 }}>
          {negocio}
        </div>

        <div className="relative z-[1] flex items-center" style={{ gap: 14 }}>
          <div className="font-bold leading-none" style={{ fontSize: 34, color: T.accent }}>
            {nota}
          </div>
          <div className="flex flex-col" style={{ gap: 4 }}>
            <EstrellasWidget nota={Number(String(nota).replace(',', '.'))} etiqueta={`${nota} / 5`} />
            <span style={{ color: T.muted, fontSize: 13 }}>{total}</span>
          </div>
        </div>
      </div>

      {conMarca ? <Marca /> : null}
    </div>
  );
}

/**
 * El widget de CARRUSEL.
 *
 * Las tarjetas son de 264×152 px FIJOS —el alto fijo es lo que hace que todas
 * queden iguales y que el texto se corte a tres líneas en vez de estirar la
 * fila—. El diseño las pintaba de 240 px y alto libre, y metía un logo de
 * Google en cada una: el widget real no lo lleva.
 *
 * Aquí el desplazamiento es el `v3-marquee` de la landing. En el widget de
 * verdad lo mueve un bucle de `requestAnimationFrame` que además se puede
 * arrastrar con el dedo; para una maqueta de escaparate no compensa portar eso,
 * y el resultado visible es el mismo.
 */
export function WidgetCarrusel({ resenas, conMarca = true }) {
  return (
    <div>
      <div className="v3-marquee">
        <div className="v3-marquee-track" style={{ gap: 12 }}>
          {[0, 1].map((copia) =>
            resenas.map((r) => (
              <TarjetaCarrusel key={`${copia}-${r.clave}`} r={r} duplicada={copia === 1} />
            )),
          )}
        </div>
      </div>

      {conMarca ? <Marca /> : null}
    </div>
  );
}

function TarjetaCarrusel({ r, duplicada }) {
  return (
    <figure
      // La lista va dos veces para que el bucle no dé un salto; la copia no
      // debe leerse dos veces.
      aria-hidden={duplicada || undefined}
      className="flex flex-col overflow-hidden m-0"
      style={{
        flex: '0 0 264px',
        width: 264,
        height: 152,
        gap: 7,
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        padding: 14,
        boxShadow: T.shadow,
        color: T.fg,
      }}
    >
      <div className="flex items-center min-w-0" style={{ gap: 10 }}>
        {/* Sin foto de perfil, el widget pinta la inicial sobre el gris del
            borde. Es el mismo respaldo que `carousel.html`. */}
        <span
          aria-hidden="true"
          className="rounded-full flex items-center justify-center font-semibold"
          style={{
            width: 34, height: 34, flex: '0 0 34px',
            background: T.border, color: T.muted, fontSize: 14,
          }}
        >
          {r.inicial}
        </span>
        <div className="min-w-0 flex-1">
          <div
            className="font-semibold truncate"
            style={{ fontSize: 13, lineHeight: 1.2 }}
          >
            {r.autor}
          </div>
          {/* Fecha en dd/mm/aaaa, que es como la formatea la plantilla. El
              diseño ponía «hace 2 días», que el widget no hace. */}
          <div style={{ color: T.muted, fontSize: 11 }}>{r.fecha}</div>
        </div>
      </div>

      <EstrellasWidget nota={r.estrellas} etiqueta={`${r.estrellas} / 5`} />

      {/* Recortado a tres líneas, como el `-webkit-line-clamp: 3` real: es lo
          que mantiene todas las tarjetas a la misma altura. */}
      <p
        className="m-0 flex-1 overflow-hidden"
        style={{
          fontSize: 13,
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {r.texto}
      </p>
    </figure>
  );
}

/**
 * El «Powered by Tapstar» del pie.
 *
 * Se pinta porque `show_branding` viene a `true` por defecto: quien pegue el
 * código lo va a ver, así que esconderlo aquí sería enseñar un widget que no es
 * el que se instala. «Powered by» va en inglés a propósito, igual que en la
 * plantilla: es el lockup de marca y no se traduce.
 */
function Marca() {
  return (
    <div className="text-center" style={{ marginTop: 10, fontSize: 11, color: T.muted }}>
      <span className="inline-flex items-center" style={{ gap: 5 }}>
        <span>Powered by</span>
        <strong style={{ color: T.fg }}>myTapstar</strong>
      </span>
    </div>
  );
}

/** La G de Google, como marca de agua sangrando por la esquina. */
function GoogleMarcaDeAgua() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="absolute pointer-events-none z-0"
      style={{ right: -14, bottom: -20, width: 112, height: 112 }}
    >
      <path fill="#4285F4" d="M23 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.2a5.3 5.3 0 0 1-2.3 3.5v2.9h3.7c2.2-2 3.4-5 3.4-8.6z" />
      <path fill="#34A853" d="M12 23.5c3.1 0 5.7-1 7.6-2.8l-3.7-2.9c-1 .7-2.3 1.1-3.9 1.1-3 0-5.5-2-6.4-4.7H1.8v3C3.7 21 7.6 23.5 12 23.5z" />
      <path fill="#FBBC05" d="M5.6 14.2a6.9 6.9 0 0 1 0-4.4v-3H1.8a11.5 11.5 0 0 0 0 10.4l3.8-3z" />
      <path fill="#EA4335" d="M12 5.1c1.7 0 3.2.6 4.4 1.7l3.3-3.3C17.7 1.6 15.1.5 12 .5 7.6.5 3.7 3 1.8 6.8l3.8 3c.9-2.7 3.4-4.7 6.4-4.7z" />
    </svg>
  );
}
